import { aboutContent } from "@/mocks/forum";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";

export default function AboutPage() {
  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "楼凤论坛",
    url: "https://www.loufengbbs.com",
    description: "楼凤论坛是汇聚用户真实体验、资讯分享与社区交流的综合性论坛平台。",
    email: aboutContent.contact.email,
    telephone: aboutContent.contact.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: "建国路88号SOHO现代城A座1508室",
      addressLocality: "北京市",
      addressRegion: "朝阳区",
      addressCountry: "CN",
    },
    sameAs: [
      "https://weibo.com/loufengbbs",
      "https://weixin.qq.com",
    ],
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: aboutContent.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const webPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于楼凤论坛",
    description: "了解楼凤论坛的品牌故事、核心价值观、发展历程和联系方式。",
    url: "https://www.loufengbbs.com/about",
    isPartOf: {
      "@type": "WebSite",
      name: "楼凤论坛",
      url: "https://www.loufengbbs.com",
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageStructuredData) }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-100">
          <Breadcrumb />
          <div className="w-full px-4 md:px-6 pb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950">关于我们</h1>
            <p className="text-sm text-foreground-500 mt-2">
              了解楼凤论坛的品牌故事与发展历程
            </p>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-8 mb-14">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-foreground-950 mb-4">品牌介绍</h2>
                <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                  {aboutContent.brandIntro}
                </p>
              </div>
              <div className="w-full lg:w-[360px] h-[280px] rounded-2xl overflow-hidden shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20open%20office%20space%20with%20team%20collaboration%20warm%20lighting%20minimalist%20design%20creative%20atmosphere%20plants%20wooden%20desks%20clean%20aesthetic%20professional%20environment&width=720&height=560&seq=about-brand-01&orientation=landscape"
                  alt="楼凤论坛办公环境"
                  className="w-full h-full object-cover"
                  title="楼凤论坛 - 品牌介绍"
                />
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">核心价值观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutContent.values.map((value, idx) => (
                  <div
                    key={idx}
                    className="p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                      <i className={`${value.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
                <div className="flex flex-col gap-6">
                  {aboutContent.milestones.map((milestone, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start gap-6 ${
                        idx % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                      }`}
                    >
                      <div className={`flex-1 ${idx % 2 === 0 ? "md:text-right" : "md:text-left"} hidden md:block`}>
                        <div className={`p-4 rounded-xl bg-background-50 border border-background-200/70`}>
                          <span className="text-sm text-foreground-600">{milestone.event}</span>
                        </div>
                      </div>
                      <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-background-50">{idx + 1}</span>
                      </div>
                      <div className="flex-1 md:hidden">
                        <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                          <span className="text-xs font-bold text-primary-600">{milestone.year}</span>
                          <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                        </div>
                      </div>
                      <div className="flex-1 hidden md:block">
                        <div className={`p-4 rounded-xl bg-background-50 border border-background-200/70`}>
                          <span className="text-xs font-bold text-primary-600">{milestone.year}</span>
                          <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">{aboutContent.contact.email}</p>
                </div>
                <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-phone-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h3>
                  <p className="text-xs text-foreground-500">{aboutContent.contact.phone}</p>
                </div>
                <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-500">{aboutContent.contact.address}</p>
                </div>
                <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-wechat-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                  <p className="text-xs text-foreground-500">{aboutContent.contact.wechat}</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">常见问题</h2>
              <div className="flex flex-col gap-3 max-w-2xl mx-auto">
                {aboutContent.faqs.map((faq, idx) => (
                  <details
                    key={idx}
                    className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden cursor-pointer"
                  >
                    <summary className="flex items-center justify-between p-4 text-sm font-medium text-foreground-900 list-none">
                      <span>{faq.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}